import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Stethoscope, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "doctor", organization_id: "", new_organization: "" });
  const [busy, setBusy] = useState(false);
  const [demos, setDemos] = useState([]);
  const [orgs, setOrgs] = useState([]);
  useEffect(() => { api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {}); api.get("/organizations/public").then((r) => setOrgs(r.data)).catch(() => {}); }, []);
  const needsOrg = ["doctor", "staff", "lab", "nutritionist"].includes(form.role);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password); else await register({ ...form, organization_id: form.organization_id && form.organization_id !== "__new__" ? form.organization_id : null, new_organization: form.organization_id === "__new__" ? form.new_organization : null });
      navigate("/");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const quick = async (d) => { setBusy(true); try { await login(d.email, d.password); navigate("/"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); } };

  return (
    <div className="min-h-screen grid lg:grid-cols-12">
      <aside className="hidden lg:flex lg:col-span-5 flex-col justify-between bg-white p-12 border-r border-slate-100">
        <img src="/carestop-logo-tight.png" alt="CareNet – Monitoring & Preventive Care" className="w-72 max-w-full self-start" data-testid="login-logo" />
        <div className="space-y-6">
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-slate-900">Decision support that a doctor signs off — every time.</h1>
          <p className="text-slate-600 text-base leading-relaxed max-w-md">Genomic markers, health check metrics and uploaded reports flow through India-first guideline rules and an AI draft. Nothing reaches a patient until a clinician approves it.</p>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-3"><Stethoscope className="w-5 h-5 text-teal-600" /> Mandatory doctor review before release</li>
            <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-teal-600" /> DPDP-aware consent gating for every stakeholder</li>
            <li className="flex gap-3"><FileCheck2 className="w-5 h-5 text-teal-600" /> Full audit trail of edits, approvals and releases</li>
          </ul>
        </div>
        <p className="text-xs text-slate-400">Decision support only. Not an autonomous diagnosis system.</p>
      </aside>
      <section className="lg:col-span-7 flex items-center justify-center p-6 sm:p-12 text-teal-50" style={{ backgroundColor: "#20534D" }}>
        <div className="w-full max-w-md space-y-8 rise">
          <div>
            <div className="bg-white rounded-2xl p-4 inline-block shadow lg:hidden mb-6"><img src="/carestop-logo-tight.png" alt="CareNet" className="h-16 w-auto" /></div>
            <p className="eyebrow text-teal-200">{mode === "login" ? "Sign in" : "Create account"}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">{mode === "login" ? "Welcome back" : "Join CareStop"}</h2>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div><Label>Full name</Label><Input data-testid="register-name-input" required value={form.name} onChange={set("name")} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{Object.keys(ROLE_LABELS).filter((r) => r !== "admin").map((r) => <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select></div>
                  {needsOrg && <div><Label>Organisation</Label>
                    <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                      <SelectTrigger data-testid="register-org-select"><SelectValue placeholder="Select clinic / hospital" /></SelectTrigger>
                      <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}>{o.name}</SelectItem>)}<SelectItem value="__new__" data-testid="org-option-new">+ Create new organisation</SelectItem></SelectContent>
                    </Select></div>}
                </div>
                {needsOrg && form.organization_id === "__new__" && <div><Label>New organisation name</Label><Input data-testid="register-new-org-input" required value={form.new_organization} onChange={set("new_organization")} placeholder="e.g. Lotus Clinic, Pune" /></div>}
              </>
            )}
            <div><Label>Email</Label><Input data-testid="login-email-input" type="email" required value={form.email} onChange={set("email")} /></div>
            <div><Label>Password</Label><Input data-testid="login-password-input" type="password" required value={form.password} onChange={set("password")} /></div>
            <Button data-testid="login-submit-button" disabled={busy} className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-full h-11">{busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}</Button>
          </form>
          <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-teal-200 hover:text-white hover:underline">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
          <div className="pt-6 border-t border-white/15">
            <p className="eyebrow text-teal-200 mb-3">Quick demo sign-in</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {demos.map((d) => (
                <button key={d.role} data-testid={`demo-login-${d.role}`} disabled={busy} onClick={() => quick(d)}
                  className="text-left rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:border-teal-400 hover:bg-teal-50 hover:shadow-sm transition-all">
                  <span className="block text-sm font-semibold text-slate-900">{ROLE_LABELS[d.role]}</span>
                  <span className="block text-xs text-slate-500 truncate mt-0.5">{d.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
